/**
 * B4 — map Sellix enrollment journeys onto Signals RNC card/detail fields.
 * Keeps existing UI shapes; fills campaign/status/step from enrollments.
 */
import { formatDateTime } from "@/lib/format";
import { flattenWorkflow } from "@/pages/signals/rnc/rncData";

const BADGE = {
  start: "START",
  email: "EMAIL",
  whatsapp: "WHATSAPP",
  sms: "SMS",
  telegram: "TELEGRAM",
  linkedin: "LINKEDIN",
  voice: "VOICE",
  wait: "WAIT",
  condition: "IF/ELSE",
  goal: "GOAL",
};

/**
 * Convert React Flow campaign snapshot → Signals FlowCanvas `{ steps }` shape.
 */
export function snapshotToSignalWorkflow(snapshot, { name, version } = {}) {
  if (!snapshot || typeof snapshot !== "object") {
    return { id: null, name: name || "Workflow", version: version || null, steps: [] };
  }
  const nodes = Array.isArray(snapshot.nodes) ? snapshot.nodes : [];
  const edges = Array.isArray(snapshot.edges) ? snapshot.edges : [];

  const ordered = _orderNodesTopologically(nodes, edges);
  const steps = ordered.map(_buildStepFromNode);

  return {
    id: version || name || "enrollment-snapshot",
    name: name || "Campaign workflow",
    version: version || null,
    steps,
  };
}

function _orderNodesTopologically(nodes, edges) {
  const byId = Object.fromEntries(
    nodes.filter((n) => n?.id).map((n) => [String(n.id), n]),
  );
  const children = Object.fromEntries(Object.keys(byId).map((id) => [id, []]));
  const targets = new Set();
  edges.forEach((e) => {
    if (e?.source && e?.target && byId[e.source] && byId[e.target]) {
      children[e.source].push(String(e.target));
      targets.add(String(e.target));
    }
  });
  let starts = Object.keys(byId).filter((id) => !targets.has(id));
  if (!starts.length) starts = Object.keys(byId).slice(0, 1);

  const ordered = [];
  const seen = new Set();
  const walk = (id) => {
    if (seen.has(id) || !byId[id]) return;
    seen.add(id);
    ordered.push(byId[id]);
    (children[id] || []).forEach(walk);
  };
  starts.forEach(walk);
  Object.keys(byId).forEach((id) => {
    if (!seen.has(id)) ordered.push(byId[id]);
  });
  return ordered;
}

function _buildStepFromNode(node) {
  const type = String(node.type || "action");
  const data = node.data && typeof node.data === "object" ? node.data : {};
  const title = data.label || type;
  const detail = data.subject || data.message || data.condition || "";
  const id = node.id != null ? String(node.id) : undefined;
  if (type === "condition" || type === "ab") {
    return { id, kind: "condition", title, badge: BADGE.condition || "IF/ELSE" };
  }
  if (type === "wait") {
    return {
      id,
      kind: "action",
      badge: BADGE.wait,
      title,
      detail: detail || "Wait",
    };
  }
  return {
    id,
    kind: "action",
    badge: BADGE[type] || String(type).toUpperCase(),
    title,
    detail: typeof detail === "string" ? detail.slice(0, 120) : "",
  };
}

function _getNextStep(uiStatus, rnc) {
  if (uiStatus.startsWith("Qualified") || uiStatus.startsWith("Unqualified")) {
    return "—";
  }
  return rnc.nextStep || "—";
}

function _getRemainingTime(enrollment, rnc) {
  if (enrollment.next_run_at) {
    return `Next: ${formatDateTime(enrollment.next_run_at)}`;
  }
  return rnc.remainingTime || "—";
}

function getEmptyJourneyProps(rnc) {
  return {
    campaignId: null,
    campaignName: null,
    workflowId: null,
    workflowAssigned: false,
    signalWorkflow: null,
    status: null,
    progress: null,
    currentStep: rnc.currentStep || null,
    stepIndex: 0,
    enrollmentId: null,
    leadStatus: null,
    exited: false,
    exitReason: null,
    stepFailures: {},
    stepSkips: {},
    lastError: null,
    channel: rnc.channel || "rnc",
    nextStep: "None",
  };
}

/**
 * Outreach timeline state for one workflow step.
 * An exited lead never has a live step: steps before the exit point stay done,
 * the exit point is marked, and everything after it is cancelled.
 * Failed sends stay red; address/channel skips stay yellow.
 */
export function timelineStepState({
  index,
  stepIndex = 0,
  status,
  exited = false,
  stepId,
  stepFailures = null,
  stepSkips = null,
}) {
  const failures = stepFailures && typeof stepFailures === "object" ? stepFailures : {};
  const skips = stepSkips && typeof stepSkips === "object" ? stepSkips : {};
  const failure = stepId && failures[stepId] ? failures[stepId] : null;
  const skip = !failure && stepId && skips[stepId] ? skips[stepId] : null;
  const failureError = failure
    ? String(failure.error || failure.reason || "Send failed")
    : null;
  const skipMessage = skip
    ? String(skip.message || skip.reason || "Step skipped")
    : null;

  const pastOrTerminal = index < stepIndex
    || String(status || "").startsWith("Qualified")
    || String(status || "").startsWith("Unqualified");

  if (failure && (pastOrTerminal || index === stepIndex)) {
    return { cls: "failed", when: "Failed", error: failureError };
  }
  if (skip && (pastOrTerminal || index === stepIndex)) {
    return { cls: "skipped", when: "Skipped", error: skipMessage };
  }

  const label = String(status || "");
  const terminal = label.startsWith("Qualified") || label.startsWith("Unqualified");
  if (terminal) return { cls: "done", when: "Completed" };

  if (exited) {
    if (index < stepIndex) return { cls: "done", when: "Completed" };
    if (index === stepIndex) return { cls: "pending", when: "Exited", stopped: true };
    return { cls: "pending", when: "Cancelled" };
  }

  if (index < stepIndex) return { cls: "done", when: "" };
  if (index === stepIndex) return { cls: "active", when: "In progress" };
  return { cls: "pending", when: "Pending" };
}

function resolveRemainingTime(rnc, enrollment, stopped) {
  if (stopped) return "—";
  if (enrollment.next_run_at) return `Next: ${formatDateTime(enrollment.next_run_at)}`;
  return rnc.remainingTime || "—";
}

export function applyJourneyToRnc(rnc, journey) {
  if (!rnc) return rnc;
  const enrollment = journey?.enrollment;
  if (!enrollment || journey?.status !== "enrolled") {
    return { ...rnc, ...getEmptyJourneyProps(rnc) };
  }

  const signalWorkflow = snapshotToSignalWorkflow(enrollment.workflow_snapshot, {
    name: enrollment.campaign_name,
  });

  const flatSteps = flattenWorkflow(signalWorkflow);

  const uiStatus = enrollment.ui_status || "";
  const exited = Boolean(enrollment.exited) || uiStatus === "Exited";
  const stopped = exited
    || uiStatus.startsWith("Qualified")
    || uiStatus.startsWith("Unqualified");
    
  let computedNextStep = "None";
  if (!stopped) {
    const nextIdx = (enrollment.step_index ?? 0) + 1;
    if (flatSteps && nextIdx < flatSteps.length) {
      computedNextStep = flatSteps[nextIdx]?.title || "Qualification Decision";
    } else {
      computedNextStep = "Qualification Decision";
    }
  }

  return {
    ...rnc,
    campaignId: enrollment.campaign_id,
    campaignName: enrollment.campaign_name,
    workflowId: enrollment.campaign_id,
    workflowAssigned: true,
    signalWorkflow,
    status: uiStatus || null,
    progress: enrollment.progress ?? null,
    currentStep: enrollment.current_step_label || rnc.currentStep,
    stepIndex: enrollment.step_index ?? 0,
    enrollmentId: enrollment.id,
    leadStatus: enrollment.lead_status,
    channel: enrollment.channel || rnc.channel || "rnc",
    assignedDate: enrollment.enrolled_at || rnc.assignedDate,
    lastActivity: enrollment.updated_at || rnc.lastActivity,
    nextStep: computedNextStep,
    exited,
    exitReason: enrollment.exit_reason || null,
    statusComment: enrollment.status_comment || null,
    stepFailures: enrollment.step_failures || {},
    stepSkips: enrollment.step_skips || {},
    lastError: enrollment.last_error || null,
    remainingTime: resolveRemainingTime(rnc, enrollment, stopped),
  };
}
